import { useRef, useState } from "react";
import { useOptionalAuth } from "../auth.js";
import { ActionConfirmation } from "../ui/ActionConfirmation.js";
import { idempotencyKey } from "./api.js";
import { bookingFailureKind } from "./attempt.js";
import { BulkCancellationDecisions } from "./BulkCancellationDecisions.js";
import { cancellationChoicesComplete, cancellationCreditChoices, chooseAllCancellationCredits, type CancellationChoices } from "./cancellationChoices.js";
import { NotifyToggle } from "./notify.js";
import { staffBooking, type BulkCancelCreditChoice, type BulkCancelPreview, type BulkCancelResult } from "./staff.js";
import { cancellationPreviewChanged, confirmsCancellationPreview, confirmsCancellationResult } from "./timeOffCancellation.js";
import { notifyStudentCreditStateChanged } from "./studentCreditEvents.js";
import { checkSelectedLessons, selectedCancellationProblem, selectedLessonLabel, type SelectedLesson } from "./selectedLessons.js";

interface CancellationOperation {
  key: string;
  lessons: SelectedLesson[];
  reason: string;
  notify: boolean;
  choices: BulkCancelCreditChoice[];
}

function startedLessonIds(preview: BulkCancelPreview): number[] {
  const now = Date.now();
  return preview.items.filter(item => {
    const startsAt = Date.parse(item.booking_start ?? "");
    // An unknown start time must not bypass the not-taught confirmation.
    return item.can_cancel && (!Number.isFinite(startsAt) || startsAt <= now);
  }).map(item => item.appointment_id);
}

export function SelectedLessonCancellation({ studentId, lessons, onBusyChange, onChanged }: {
  studentId: string;
  lessons: SelectedLesson[];
  onBusyChange: (busy: boolean) => void;
  onChanged: () => void;
}) {
  const auth = useOptionalAuth();
  const storageKey = `wmf:selected-lesson-cancellation:${auth?.me?.user.id ?? "staff"}:${studentId}`;
  const [recovery] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey) ?? "null") as CancellationOperation | null;
      if (saved && (!saved.key || typeof saved.reason !== "string" || typeof saved.notify !== "boolean" || !Array.isArray(saved.choices) || !Array.isArray(saved.lessons) || !saved.lessons.length || saved.lessons.length > 50 || saved.lessons.some(l => !l.bookingId || !Number.isSafeInteger(l.appointmentId)))) throw new Error();
      return { operation: saved, error: null };
    } catch { return { operation: null, error: "The saved cancellation could not be read. Ask the office to check these lessons before starting another cancellation." }; }
  });
  const [operation, setOperation] = useState<CancellationOperation | null>(recovery.operation);
  const [preview, setPreview] = useState<BulkCancelPreview | null>(null);
  const [choices, setChoices] = useState<CancellationChoices>({});
  const [confirmedNotTaught, setConfirmedNotTaught] = useState<number[]>([]);
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(false);
  const [result, setResult] = useState<BulkCancelResult | null>(null);
  const [problem, setProblem] = useState<string | null>(recovery.error);
  const [busy, setBusy] = useState(false);
  const locked = useRef(false);
  const ids = lessons.map(l => l.appointmentId);
  const startedIds = preview ? startedLessonIds(preview) : [];
  const startedLessonsConfirmed = startedIds.every(id => confirmedNotTaught.includes(id));

  function start() { locked.current = true; setBusy(true); onBusyChange(true); setProblem(null); }
  function finish() { locked.current = false; setBusy(false); onBusyChange(false); }

  async function checkIdentity() {
    const rows = await checkSelectedLessons(studentId, lessons);
    const blocked = rows.map(r => ({ lesson: r.lesson, problem: selectedCancellationProblem(r) })).filter(r => r.problem);
    if (blocked.length) throw new Error(blocked.map(r => `${selectedLessonLabel(r.lesson)}: ${r.problem}`).join(" "));
  }

  async function look() {
    if (locked.current || recovery.error) return;
    start();
    setPreview(null);
    setChoices({});
    setConfirmedNotTaught([]);
    try {
      await checkIdentity();
      const next = await staffBooking.bulkCancelPreview(ids, reason.trim());
      if (!confirmsCancellationPreview(next, ids)) throw new Error("The preview did not match the selected lessons. Refresh and try again.");
      setPreview(next);
    } catch (cause) { setProblem(cause instanceof Error ? cause.message : "The cancellation could not be previewed."); }
    finally { finish(); }
  }

  function completed(next: BulkCancelResult, attempt: CancellationOperation) {
    if (!confirmsCancellationResult(next, attempt.lessons.map(l => l.appointmentId))) throw new Error("The response did not confirm every selected cancellation. Check this same attempt before trying again.");
    sessionStorage.removeItem(storageKey);
    setResult(next);
    notifyStudentCreditStateChanged(studentId);
    onChanged();
  }

  async function confirm(retry?: CancellationOperation) {
    if (locked.current || recovery.error || (!retry && (!preview || !cancellationChoicesComplete(preview, choices) || !startedLessonsConfirmed))) return;
    start();
    let attempt = retry;
    try {
      if (!attempt) {
        await checkIdentity();
        const fresh = await staffBooking.bulkCancelPreview(ids, reason.trim());
        if (!confirmsCancellationPreview(fresh, ids)) throw new Error("The preview did not match the selected lessons.");
        // Notice hours move as time passes. Compare only the decision and
        // financial fields that the user actually approved.
        const signature = (p: BulkCancelPreview) => JSON.stringify(p.items.map(({ notice_period_hours: _hours, ...item }) => item));
        if (signature(fresh) !== signature(preview!)) {
          setPreview(fresh);
          setChoices({});
          setConfirmedNotTaught([]);
          throw new Error("The cancellation outcome changed. Review the new preview and confirm again.");
        }
        // A lesson may start while the preview is open or its refresh is in flight.
        if (startedLessonIds(fresh).some(id => !confirmedNotTaught.includes(id))) {
          setPreview(fresh);
          setConfirmedNotTaught([]);
          throw new Error("A selected lesson has now started. Confirm that the past or started lessons were not taught before cancelling.");
        }
        attempt = { key: idempotencyKey(), lessons, reason: reason.trim(), notify, choices: cancellationCreditChoices(fresh, choices) };
        sessionStorage.setItem(storageKey, JSON.stringify(attempt));
        setOperation(attempt);
      }
      completed(await staffBooking.bulkCancel(attempt.lessons.map(l => l.appointmentId), attempt.reason, attempt.key, attempt.notify, attempt.choices), attempt);
    } catch (cause) {
      if (attempt && (cancellationPreviewChanged(cause) || bookingFailureKind(cause) === "rejected")) {
        sessionStorage.removeItem(storageKey);
        setOperation(null);
        setPreview(null);
        setChoices({});
        setConfirmedNotTaught([]);
      }
      setProblem(cause instanceof Error ? cause.message : "The cancellation outcome could not be confirmed.");
    } finally { finish(); }
  }

  async function recover() {
    if (locked.current || !operation) return;
    start();
    try {
      const status = await staffBooking.bulkCancelOperation(operation.key);
      if (status.idempotency_key !== operation.key) throw new Error("The cancellation receipt did not match this attempt.");
      if (status.state === "completed" && status.result) completed(status.result, operation);
      else setProblem("This cancellation is still being checked. Keep this attempt open and check again.");
    } catch (cause) { setProblem(cause instanceof Error ? cause.message : "The cancellation could not be checked."); }
    finally { finish(); }
  }

  const displayed = operation?.lessons ?? lessons;
  return <div className="stack">
    {problem && <div className="error" role="alert">{problem}</div>}
    <ul className="selected-lesson-results">{displayed.map(l => <li key={l.appointmentId}><strong>{selectedLessonLabel(l)}</strong>{result && <span>{(() => {
      const row = result.results.find(r => r.appointment_id === l.appointmentId);
      return row?.success ? `Cancelled${row.credit_review_required ? " · Credit/payment needs review" : ""}` : row?.message ?? "Not confirmed cancelled";
    })()}</span>}</li>)}</ul>
    {result ? <ActionConfirmation variant={result.failed_count || result.results.some(r => r.credit_review_required) ? "attention" : "success"}
      title="Cancellation results" details={[
        { value: `${result.cancelled_count} cancelled; ${result.already_cancelled_count} already cancelled; ${result.failed_count} not confirmed cancelled.` },
        { value: `${result.credits_restored} credits returned; ${result.credits_granted ?? 0} rebooking credits issued.` },
        { value: operation?.notify ? "Family notifications requested." : "Family notifications not requested." },
      ]} /> : operation ? <>
      <ActionConfirmation variant="uncertain" title="Cancellation outcome needs checking" details={[{ value: "Check this saved attempt before starting another cancellation. Retrying uses the same lessons, credit choices and notification setting." }]} />
      <button type="button" disabled={busy} onClick={() => void recover()}>Check cancellation status</button>
      <button type="button" disabled={busy} onClick={() => void confirm(operation)}>Retry same cancellation</button>
    </> : <>
      <p className="small muted">Cancels only the selected lessons. Any recurring booking arrangement stays active.</p>
      <label className="stack">Note (optional)
        <input value={reason} maxLength={500} disabled={busy} onChange={e => { setReason(e.target.value); setPreview(null); setChoices({}); setConfirmedNotTaught([]); }} />
      </label>
      <NotifyToggle label="Send cancellation notifications to the family" checked={notify} onChange={setNotify} warn disabled={busy} />
      <button type="button" disabled={busy || !!recovery.error} onClick={() => void look()}>Preview cancellation</button>
      {preview && <BulkCancellationDecisions preview={preview} choices={choices} busy={busy}
        onChoice={(id, choice) => setChoices(c => ({ ...c, [id]: choice }))}
        onChooseAll={choice => setChoices(c => chooseAllCancellationCredits(preview, c, choice))}>
        {startedIds.length > 0 && <div className="notice selected-lesson-started-warning">
          <p><strong>Confirm {startedIds.length} selected lesson{startedIds.length === 1 ? " was" : "s were"} not taught.</strong> Past lessons, lessons in progress and lessons with an unconfirmed start time need this confirmation. Do not cancel lessons that were taught. Any credit you choose to return or issue becomes available for another lesson.</p>
          <label><input type="checkbox" name="confirm-not-taught" checked={startedLessonsConfirmed} disabled={busy} onChange={e => setConfirmedNotTaught(e.target.checked ? startedIds : [])} /> I confirm these lessons were not taught and should be cancelled.</label>
        </div>}
        <button className="danger" type="button" disabled={busy || !preview.cancellable_count || !cancellationChoicesComplete(preview, choices) || !startedLessonsConfirmed} onClick={() => void confirm()}>
          {busy ? "Cancelling…" : `Cancel ${preview.cancellable_count} selected lesson${preview.cancellable_count === 1 ? "" : "s"}`}
        </button>
      </BulkCancellationDecisions>}
    </>}
  </div>;
}
